import { Inject, Injectable } from '@angular/core';
import { ConfigService } from '../../config/config.service';
import { CONSTANTS } from '../../config/constants.module';
import esriConfig from '@arcgis/core/config';
import { CaseTransform } from '../../shared/util/case-transform';
import { ArrayBufferToJsonConverter } from '../../shared/util/array-buffer-to-json-converter';

/**
 * @class EsriService
 * @version 0.0.1
 * @since September 2017
 * @author Tuan La
 * @description The EsriService is used for loading the Esri API
 * into the app. Befor the home-state is entered, the {@class EsriApiResolver}
 * kicked the {@function EsriService.load} and load all Esri API modules into
 * the app.
 */
@Injectable({
  providedIn: 'root',
})
export class EsriService {
  constructor(private _configService: ConfigService,
              @Inject(CONSTANTS) private _CONST: any) {}

  /**
   * Checks if the origin of an url is in the list of server,
   * to find out if credentials are required to request the url
   * @param url the url to check
   * @param servers the list of the configured servers, which need Credentials
   */
  private static serverNeedsCredentials(url: string, servers: string[]): boolean {
    return servers.some(server => url.includes(server));
  }

  public init() {
    // set trust core servers
    for (let i = 0; i < this._configService.appConfig.esri.trustedServers.length; i++) {
      esriConfig.request.trustedServers.push(this._configService.appConfig.esri.trustedServers[i]);
    }

    this.esriRequestInterceptor(esriConfig);
  }

  /**
   * intercepts every esriRequest to add withCredentials if needed
   * @param currentEsriConfig the config to add the interceptor to
   */
  private esriRequestInterceptor(currentEsriConfig: __esri.config): any {
    const credentialsRequiredServers = this._configService.appConfig.esri
        .credentialsRequiredServers;
    // case transform interceptor
    // order matters: the first interceptor with matching URL is taken
    currentEsriConfig.request.interceptors.push({
      urls: /\/(FeatureServer|MapServer)(\/|$)/, // allow only URLs which look like e.g. '*/FeatureServer/3' or '*/MapServer'
      before(params) {
        if (EsriService.serverNeedsCredentials(params.url, credentialsRequiredServers)) {
          params.requestOptions.withCredentials = true;
        }

        if (CaseTransform.canRequestBeTransformed(params.requestOptions)) {
          CaseTransform.transformRequestQueryToLowerCase(params.requestOptions);
        }
      },
      after(response) {
        if (response?.requestOptions?.query?.f === 'pbf') {
          // ignore pbf requests
          return;
        }

        // the response must be in JSON format
        response.data.supportedQueryFormats = 'JSON';

        let data = response?.data;
        let convertedDataToJson = false;
        if (response?.requestOptions?.responseType === 'array-buffer') {
          try {
            // convert the response data to a JSON object
            data = ArrayBufferToJsonConverter.convertArrayBufferToJson(data);
            convertedDataToJson = true;
          } catch (error) {
            console.error(`Cannot convert Esri array buffer to JSON: ${error}`);
          }
        }

        if (CaseTransform.canDataObjectBeTransformed(data)) {
          // case transform all necessary response data
          CaseTransform.transformDataObjectToUpperCase(data);

          if (convertedDataToJson) {
            // convert the modified JSON object back to an array buffer
            const buffer = ArrayBufferToJsonConverter.convertJsonToArrayBuffer(data);
            response.data = buffer;
          }
        }
      },
      error() {},
    });

    // general credentials interceptor
    // order matters: the first interceptor with matching URL is taken
    currentEsriConfig.request.interceptors.push({
      before(params) {
        if (EsriService.serverNeedsCredentials(params.url, credentialsRequiredServers)) {
          params.requestOptions.withCredentials = true;
        }
      },
      error() {},
    });
  }
}
